import { LitElement, html, css } from 'lit';
import { api } from '../api.js';
import { alertDialog } from '../utils/confirm-dialog.js';
import { normalizeExtractionProfile } from '@mv/models';

/**
 * Prompt-version editor (/admin/validation/model/:id). Title + big textarea with the
 * system prompt sent to the extraction model. Editable + Save — UNLESS this version is
 * the active production prompt, which is read-only (the backend also enforces this with
 * a 409). To change production: create a new version and activate it on /admin/settings/model.
 */
export class ValidationModelEditor extends LitElement {
  static properties = {
    versionId: { type: String },
    _data: { state: true },
    _text: { state: true },
    _title: { state: true },
    _loading: { state: true },
    _saving: { state: true },
  };

  constructor() {
    super();
    this.versionId = '';
    this._data = null;
    this._text = '';
    this._title = '';
    this._loading = true;
    this._saving = false;
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  async _load() {
    this._loading = true;
    try {
      const d = await api.getPromptVersion(this.versionId);
      this._data = d;
      this._text = d.systemPrompt || '';
      this._title = d.title || '';
    } catch (e) {
      this._data = { error: e.message };
    } finally {
      this._loading = false;
    }
  }

  get _dirty() { return this._data && !this._data.isActive && (this._text !== (this._data.systemPrompt || '') || this._title !== (this._data.title || '')); }

  /** Terug naar de subpagina van het model waar deze promptversie bij hoort. */
  _back() {
    const profile = normalizeExtractionProfile(this._data?.profile);
    window.history.pushState({}, '', `/admin/validation/${profile}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  async _save() {
    if (!this._dirty || this._saving) return;
    this._saving = true;
    try {
      await api.updatePromptVersion(this.versionId, { title: this._title.trim(), systemPrompt: this._text });
      await this._load();
      alertDialog({ title: 'Opgeslagen', message: 'De promptversie is bijgewerkt.' });
    } catch (e) {
      alertDialog({ title: 'Fout', message: e.message });
    } finally {
      this._saving = false;
    }
  }

  render() {
    if (this._loading) return html`<p class="muted">Laden…</p>`;
    if (this._data?.error) return html`<div><button class="btn" @click=${this._back}>← Terug</button><p class="muted">Fout: ${this._data.error}</p></div>`;
    const locked = !!this._data.isActive;
    return html`
      <button class="btn back" @click=${this._back}>← Terug naar validatie</button>
      <div class="head">
        <input class="title" .value=${this._title} ?disabled=${locked} @input=${(e) => this._title = e.target.value} placeholder="Versienaam (bijv. V0.2)">
        ${locked ? html`<span class="lock">🔒 Actieve productie-prompt — read-only</span>` : ''}
      </div>
      ${locked ? html`<div class="warn">Dit is de prompt die nu in productie draait en kan niet worden bewerkt. Maak een nieuwe versie aan om te wijzigen.</div>` : ''}
      <label class="lbl">System prompt (wordt naar het extractiemodel gestuurd)</label>
      <textarea .value=${this._text} ?disabled=${locked || this._saving} @input=${(e) => this._text = e.target.value} spellcheck="false"></textarea>
      ${locked ? '' : html`
        <div class="actions">
          <button class="btn primary" @click=${this._save} ?disabled=${!this._dirty || this._saving}>${this._saving ? 'Opslaan…' : 'Opslaan'}</button>
          ${this._dirty ? html`<button class="btn" @click=${() => { this._text = this._data.systemPrompt || ''; this._title = this._data.title || ''; }}>Herstel</button>` : ''}
        </div>`}
    `;
  }

  static styles = css`
    :host { display: block; }
    .muted { color: var(--mv-text-muted); }
    .btn { padding: .5rem 1rem; border-radius: .5rem; border: 1px solid var(--mv-border, #eff2f5); background: var(--mv-surface, #fff); color: var(--mv-text); font-weight: 600; cursor: pointer; font-size: .88rem; }
    .btn.primary { background: var(--mv-primary, #4b9d35); color: #fff; border-color: transparent; }
    .btn:disabled { opacity: .5; cursor: not-allowed; }
    .btn.back { margin-bottom: 1rem; }
    .head { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; margin-bottom: .5rem; }
    .title { font-size: 1.3rem; font-weight: 700; color: var(--mv-text); border: 1px solid transparent; border-radius: .4rem; padding: .25rem .4rem; background: transparent; font-family: inherit; }
    .title:focus { border-color: var(--mv-border); outline: none; background: var(--mv-surface); }
    .title:disabled { color: var(--mv-text); }
    .lock { font-size: .8rem; font-weight: 700; color: #a9821a; }
    .warn { background: #f6c34322; border: 1px solid #f6c34355; color: #8a6d1a; border-radius: .5rem; padding: .6rem .8rem; font-size: .85rem; margin-bottom: 1rem; }
    .lbl { display: block; font-size: .8rem; font-weight: 700; color: var(--mv-text-sub, #5e6278); margin-bottom: .35rem; }
    textarea {
      width: 100%; box-sizing: border-box; min-height: 60vh; resize: vertical;
      border: 1px solid var(--mv-border, #eff2f5); border-radius: .5rem; padding: .8rem;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: .82rem; line-height: 1.5;
      color: var(--mv-text); background: var(--mv-surface, #fff); outline: none;
    }
    textarea:focus { border-color: var(--mv-primary); }
    .actions { display: flex; gap: .6rem; margin-top: 1rem; }
  `;
}

customElements.define('validation-model-editor', ValidationModelEditor);
